import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams, Link, useSearchParams, } from "react-router-dom";
import API from "../api/api";
import { AuthContext } from "../context/AuthContext";

const API_BASE_URL = "https://warfc.com/api";

// =========================================================
// HELPERS
// =========================================================

const unwrap = (res) => res?.data ?? res;

// =========================================================
// FIGHTER AGE / ELIGIBILITY HELPERS
// =========================================================

const getFighterGender = (fighter) => {
  return String(fighter?.gender || "").trim().toLowerCase();
};

const getFighterDob = (fighter) => {
  return fighter?.dob || fighter?.date_of_birth || null;
};

const calculateAgeOnDate = (dob, competitionDate) => {
  if (!dob || !competitionDate) return null;

  const birthDate = new Date(dob);
  const eventDate = new Date(competitionDate);

  if (
    Number.isNaN(birthDate.getTime()) ||
    Number.isNaN(eventDate.getTime())
  ) {
    return null;
  }

  let age =
    eventDate.getUTCFullYear() -
    birthDate.getUTCFullYear();

  const eventMonth = eventDate.getUTCMonth();
  const birthMonth = birthDate.getUTCMonth();

  if (
    eventMonth < birthMonth ||
    (
      eventMonth === birthMonth &&
      eventDate.getUTCDate() < birthDate.getUTCDate()
    )
  ) {
    age--;
  }

  return age;
};


const getDivisionMinAge = (division) => {
  const value =
    division?.min_age ??
    division?.age_min ??
    null;

  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : null;
};

const getDivisionMaxAge = (division) => {
  const value =
    division?.max_age ??
    division?.age_max ??
    null;

  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : null;
};

const divisionContainsAge = (division, age) => {
  if (age == null) return false;

  const min = getDivisionMinAge(division);
  const max = getDivisionMaxAge(division);

  if (min == null || max == null) {
    return false;
  }

  return age >= min && age <= max;
};

const divisionCanBeSelectedByAge = (division, age) => {
  if (age == null) return false;

  const min = getDivisionMinAge(division);
  const max = getDivisionMaxAge(division);

  if (min == null || max == null) return false;

  // Fighter can compete in their natural age group
  // or move up to the next older age group.
  return age >= min - 1 && age <= max;
};

const normalizeArray = (data, keys = []) => {
  if (Array.isArray(data)) return data;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) {
      return data[key];
    }
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
};

const getAgeDivisionId = (division) =>
  division?.agedivid ??
  division?.age_division_id ??
  division?.division_id ??
  division?.id;

const getAgeDivisionName = (division) =>
  division?.name ??
  division?.age_division ??
  division?.age_group ??
  division?.label ??
  `Age Division #${getAgeDivisionId(division)}`;

const getWeightId = (weight) =>
  weight?.id ??
  weight?.division_weightclass_id;

const getWeightName = (weight) =>
  weight?.weightclass_name ??
  weight?.name ??
  weight?.weight_class ??
  "Weight Class";

// =========================================================
// DISPLAY HELPERS
// =========================================================

const formatTournamentDate = (tournament) => {
  const start =
    tournament?.start_date ??
    tournament?.startDate ??
    tournament?.date ??
    tournament?.event_date;

  const end =
    tournament?.end_date ??
    tournament?.endDate;

  if (!start) return null;

  const format = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);

    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return end && String(end) !== String(start)
    ? `${format(start)} – ${format(end)}`
    : format(start);
};

const getTournamentTitle = (tournament) =>
  tournament?.name ??
  tournament?.tournament_name ??
  tournament?.title ??
  tournament?.event_name ??
  "WAR FC TOURNAMENT";

const getRegistrationWeight = (registration) =>
  registration?.weightclass_name ??
  registration?.weight_class ??
  registration?.weightclass ??
  "Not selected";

const getRegistrationAge = (registration) =>
  registration?.age_division ??
  registration?.age_group ??
  registration?.ageDivision ??
  "Not selected";

const getRegistrationCategory = (registration) =>
  registration?.type === "altan_garaa"
    ? "ALTAN GARAA"
    : registration?.type
    ? "ҮНДСЭН"
    : registration?.category === "altan_garaa"
    ? "ALTAN GARAA"
    : "ҮНДСЭН";

const getRegistrationGender = (registration) =>
  String(
    registration?.registration_gender ??
      registration?.gender ??
      ""
  ).toUpperCase() || "—";

// =========================================================
// MAIN COMPONENT
// =========================================================

export default function TournamentPublicRegister() {
  const { tournamentId } = useParams();
  const { user } = useContext(AuthContext);

  const [searchParams] = useSearchParams();

  const adminEditMode =
    searchParams.get("adminEdit") === "1";

  const adminEditFighterId =
    searchParams.get("fighter");

  const isAdmin =
    user?.role === "admin";

  const canAccessRegistration =
    (adminEditMode && isAdmin) ||
    !!user?.fighter_id;

  const registrationOptionsRef =
    React.useRef(null);

  // =======================================================
  // TOURNAMENT
  // =======================================================

  const [tournament, setTournament] = useState(null);

  const [ageDivisions, setAgeDivisions] = useState([]);

  const [weightGroups, setWeightGroups] = useState({
    main: [],
    altan_garaa: [],
  });

  // =======================================================
  // REGISTRATION FORM
  // =======================================================

  const [category, setCategory] = useState("");

  const [gender, setGender] = useState("");
const [ageGroup, setAgeGroup] = useState("");

  const [selectedWeight, setSelectedWeight] =
    useState("");

  // =======================================================
  // CLUB COACH
  // =======================================================

  const [isClubCoach, setIsClubCoach] =
    useState(false);

  const [coachClub, setCoachClub] = useState(null);

const [fighterSearch, setFighterSearch] = useState("");

  const [clubFighters, setClubFighters] =
    useState([]);

  const [selectedFighterId, setSelectedFighterId] =
    useState("");
const [profileFighter, setProfileFighter] = useState(null);

  const [loadingCoach, setLoadingCoach] =
    useState(false);

  // =======================================================
  // LOADING
  // =======================================================

  const [loading, setLoading] = useState(true);

  const [loadingAges, setLoadingAges] =
    useState(false);

  const [loadingWeights, setLoadingWeights] =
    useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const [unregistering, setUnregistering] =
    useState(false);

    const [copiedText, setCopiedText] = useState("");

  // =======================================================
  // REGISTRATION STATUS
  // =======================================================

  const [
    existingRegistration,
    setExistingRegistration,
  ] = useState(null);

  const [success, setSuccess] = useState(null);

  const [error, setError] = useState("");

  const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);

    setCopiedText(text);

    setTimeout(() => {
      setCopiedText("");
    }, 2000);
  } catch (err) {
    console.error("Copy failed:", err);
  }
};

  // =======================================================
  // SELECTED FIGHTER
  // =======================================================

  const selectedFighter = isClubCoach
  ? clubFighters.find(
      (fighter) =>
        Number(fighter.fighter_id) ===
        Number(selectedFighterId)
    )
  : null;

const registeringFighter = profileFighter;

  const tournamentStartDate =
  tournament?.startdate ??
  tournament?.start_date ??
  tournament?.startDate ??
  tournament?.date ??
  null;
 
  const filteredCoachFighters = useMemo(() => {
  if (!Array.isArray(clubFighters)) return [];

  const query = fighterSearch.trim().toLowerCase();

  if (!query) return clubFighters;

  return clubFighters.filter((fighter) => {
    const fullName = `${fighter.name || ""} ${fighter.surname || ""}`
      .toLowerCase();

    return fullName.includes(query);
  });
}, [clubFighters, fighterSearch]);

const fighterAge = useMemo(() => {
  if (!registeringFighter) return null;

  const dob = getFighterDob(registeringFighter);

  return calculateAgeOnDate(
    dob,
    tournamentStartDate
  );
}, [
  registeringFighter,
  tournamentStartDate,
]);
 
  const registrationFighterId = adminEditMode
  ? adminEditFighterId
  : isClubCoach
    ? selectedFighterId
    : user?.fighter_id;

  // =======================================================
  // TOURNAMENT INITIALIZATION
  // =======================================================

  useEffect(() => {
    let cancelled = false;

    async function loadTournament() {
      if (!tournamentId) {
        setError("Тэмцээний мэдээлэл олдсонгүй.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const res = await API.get(`/tournaments/${tournamentId}`);
        const raw = unwrap(res);
        const data = raw?.tournament ?? raw?.data ?? raw;

        if (!cancelled) {
          setTournament({
            ...(data || {}),
            id:
              data?.id ??
              data?.tournament_id ??
              data?.tournamentid ??
              Number(tournamentId),
          });
        }
      } catch (err) {
        console.error("Tournament load error:", err);

        if (!cancelled) {
          setTournament({
            id: Number(tournamentId),
            name: "",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTournament();

    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

 

// =========================================================
// AUTO SET FIGHTER GENDER
// =========================================================

useEffect(() => {
  // ADMIN EDIT:
  // Existing registration is the source of truth.
  if (adminEditMode) {
    return;
  }

  if (!registeringFighter) {
    setGender("");
    setAgeGroup("");
    return;
  }

  const fighterGender =
    getFighterGender(registeringFighter);

  setGender(fighterGender);

}, [
  registeringFighter,
  adminEditMode,
]);

  // =======================================================
  // LOAD AGE DIVISIONS
  // =======================================================

  useEffect(() => {
    let cancelled = false;

    async function loadAgeDivisions() {
      if (!tournamentId) return;

      try {
        setLoadingAges(true);
        setError("");

        const res = await API.get(
          `/tournaments/${tournamentId}/age-divisions`
        );

        const data = unwrap(res);

        const divisions = normalizeArray(data, [
          "divisions",
          "ageDivisions",
          "age_divisions",
        ]);

        if (!cancelled) {
          setAgeDivisions(divisions);
        }
      } catch (err) {
        console.error(
          "Age divisions load error:",
          err
        );

        if (!cancelled) {
          setAgeDivisions([]);

          setError(
            err?.message ||
              "Насны ангилал ачааллахад алдаа гарлаа."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingAges(false);
        }
      }
    }

    loadAgeDivisions();

    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  // =======================================================
  // SORT AGE DIVISIONS
  // =======================================================

  const sortedAgeDivisions =
    useMemo(() => {
      const getStartAge = (division) => {
        const name =
          getAgeDivisionName(
            division
          );

        const match =
          String(name).match(
            /\d+/
          );

        return match
          ? Number(match[0])
          : 999;
      };

      return [...ageDivisions].sort(
        (a, b) =>
          getStartAge(a) -
          getStartAge(b)
      );
    }, [ageDivisions]);

 // =========================================================
// ELIGIBLE AGE DIVISIONS
// =========================================================

const eligibleAgeDivisions = useMemo(() => {
  if (adminEditMode) {
    return sortedAgeDivisions;
  }

  if (fighterAge == null) {
    return [];
  }

  return sortedAgeDivisions.filter((division) => {
    // ALTAN GARAA does not allow 6–7
    if (
      category === "altan_garaa" &&
      getDivisionMinAge(division) === 6 &&
      getDivisionMaxAge(division) === 7
    ) {
      return false;
    }

    const min = getDivisionMinAge(division);
    const max = getDivisionMaxAge(division);

    if (min == null || max == null) {
      return false;
    }

    // Own age division
    const isNaturalDivision =
      fighterAge >= min &&
      fighterAge <= max;

    // One age division above
    const isNextOlderDivision =
      fighterAge === min - 1;

    return (
      isNaturalDivision ||
      isNextOlderDivision
    );
  });
}, [
  sortedAgeDivisions,
  fighterAge,
  adminEditMode,
  category,
]);

// =========================================================
// AUTO SELECT NATURAL AGE DIVISION
// =========================================================

useEffect(() => {
  if (adminEditMode) return;

  if (
    fighterAge == null ||
    !eligibleAgeDivisions.length
  ) {
    setAgeGroup("");
    return;
  }

  const naturalDivision =
    eligibleAgeDivisions.find(
      (division) =>
        divisionContainsAge(
          division,
          fighterAge
        )
    );

  if (naturalDivision) {
    setAgeGroup(
      String(
        getAgeDivisionId(naturalDivision)
      )
    );
  } else {
    setAgeGroup(
      String(
        getAgeDivisionId(
          eligibleAgeDivisions[0]
        )
      )
    );
  }
}, [
  fighterAge,
  eligibleAgeDivisions,
  adminEditMode,
]);

  // =======================================================
  // DETECT CLUB COACH
  // =======================================================

  useEffect(() => {
    let cancelled = false;

    async function detectClubCoach() {
      if (!user?.fighter_id) {
        setIsClubCoach(false);
        setCoachClub(null);
        setClubFighters([]);
        setSelectedFighterId("");
        return;
      }

      try {
        setLoadingCoach(true);

        const res = await API.get("/clubs");

        const clubs = normalizeArray(
          unwrap(res)
        );

        const club = clubs.find(
          (c) =>
            Number(c.coach_fighter_id) ===
            Number(user.fighter_id)
        );

        // ---------------------------------------------------
        // NOT A CLUB COACH
        // ---------------------------------------------------

        if (!club) {
          if (!cancelled) {
            setIsClubCoach(false);
            setCoachClub(null);
            setClubFighters([]);

            setSelectedFighterId(
              String(user.fighter_id)
            );
          }

          return;
        }

        // ---------------------------------------------------
        // CLUB COACH
        // ---------------------------------------------------

        const rosterRes = await API.get(
          `/clubs/${club.club_id}/fighters`
        );

        const fighters = normalizeArray(
          unwrap(rosterRes)
        );

        if (!cancelled) {
          setIsClubCoach(true);
          setCoachClub(club);
          setClubFighters(fighters);

          // Coach must explicitly choose fighter.
          setSelectedFighterId("");
        }
      } catch (err) {
        console.error(
          "Club coach detection error:",
          err
        );

        if (!cancelled) {
          // Fail safely.
          // If coach information cannot be loaded,
          // treat the user as a normal fighter.
          setIsClubCoach(false);
          setCoachClub(null);
          setClubFighters([]);

          setSelectedFighterId(
            String(user.fighter_id)
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingCoach(false);
        }
      }
    }

    detectClubCoach();

    return () => {
      cancelled = true;
    };
  }, [user?.fighter_id]);

  // =======================================================
  // LOAD WEIGHTS
  // =======================================================

  useEffect(() => {
    const divisionId = Number(ageGroup);

    if (!divisionId || !gender) {
      setWeightGroups({
        main: [],
        altan_garaa: [],
      });

      setSelectedWeight("");

      return;
    }

    let cancelled = false;

    async function fetchWeightclasses() {
      try {
        setLoadingWeights(true);
        setError("");
        setSelectedWeight("");

        const selectedDivision =
          ageDivisions.find(
            (d) =>
              Number(getAgeDivisionId(d)) ===
              divisionId
          );

        const age =
          selectedDivision?.min_age ?? 0;

        const res = await API.get(
  `/tournaments/division/${divisionId}/weightclasses`,
  {
    params: {
      gender,
      age,
      tournamentId
    }
  }
);

        const data = unwrap(res);

        let main = Array.isArray(data?.main)
          ? data.main
          : [];

        let altan_garaa = Array.isArray(
          data?.altan_garaa
        )
          ? data.altan_garaa
          : [];

        // ---------------------------------------------------
        // FALLBACK
        // ---------------------------------------------------

        if (
          !main.length &&
          !altan_garaa.length &&
          Array.isArray(data)
        ) {
          main = data.filter(
            (w) => w.type === "main"
          );

          altan_garaa = data.filter(
            (w) => w.type !== "main"
          );
        }

        // ---------------------------------------------------
        // REMOVE DUPLICATES
        // ---------------------------------------------------

        const clean = (items) => {
          const seen = new Set();

          return items.filter((item) => {
            const id = getWeightId(item);

            if (id == null) return false;

            const key = String(id);

            if (seen.has(key)) {
              return false;
            }

            seen.add(key);

            return true;
          });
        };

        main = clean(main);
        altan_garaa = clean(
          altan_garaa
        );

        // ---------------------------------------------------
        // SORT WEIGHTS
        // ---------------------------------------------------

        const sortWeights = (items) => {
          return [...items].sort((a, b) => {
            const an = getWeightName(a);
            const bn = getWeightName(b);

            const ap = an.includes("+");
            const bp = bn.includes("+");

            if (ap !== bp) {
              return ap ? 1 : -1;
            }

            const aw = Number(a?.weight);
            const bw = Number(b?.weight);

            if (
              Number.isFinite(aw) &&
              Number.isFinite(bw)
            ) {
              return aw - bw;
            }

            return an.localeCompare(
              bn,
              undefined,
              {
                numeric: true,
              }
            );
          });
        };

        main = sortWeights(main);
        altan_garaa =
          sortWeights(altan_garaa);

        if (cancelled) return;

        setWeightGroups({
          main,
          altan_garaa,
        });
      } catch (err) {
        console.error(
          "Weightclasses load error:",
          err?.response?.data || err
        );

        if (!cancelled) {
          setWeightGroups({
            main: [],
            altan_garaa: [],
          });

          setSelectedWeight("");

          setError(
            err?.message ||
              "Жингийн ангилал ачааллахад алдаа гарлаа."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingWeights(false);
        }
      }
    }

    fetchWeightclasses();

    return () => {
      cancelled = true;
    };
  }, [
    gender,
    ageGroup,
    ageDivisions,
  ]);

  // =======================================================
  // CHECK EXISTING REGISTRATION
  // =======================================================

  useEffect(() => {
    let cancelled = false;

    async function checkRegistration() {
      if (
        !tournamentId ||
        !registrationFighterId
      ) {
        setExistingRegistration(null);
        return;
      }

      try {
        const res = await API.get(
          `/tournaments/${tournamentId}/registration`
        );

        const data = unwrap(res);

        const registrations =
          normalizeArray(data, [
            "registrations",
          ]);

        const mine =
          registrations.find(
            (registration) =>
              Number(
                registration.fighter_id
              ) ===
              Number(
                registrationFighterId
              )
          );

        if (!cancelled) {
          setExistingRegistration(
            mine || null
          );
        }
      } catch (err) {
        console.error(
          "Registration status error:",
          err
        );

        if (!cancelled) {
          setExistingRegistration(null);
        }
      }
    }

    checkRegistration();

    return () => {
      cancelled = true;
    };
  }, [
    tournamentId,
    registrationFighterId,
  ]);

  // =========================================================
// LOAD FULL FIGHTER PROFILE
// =========================================================

useEffect(() => {
  let cancelled = false;

  async function loadFighterProfile() {
    const fighterId = adminEditMode
  ? adminEditFighterId
  : isClubCoach
    ? selectedFighterId
    : user?.fighter_id;

    if (!fighterId) {
      setProfileFighter(null);
      return;
    }

    try {
      const res = await API.get(`/fighters/${fighterId}`);

      const data = unwrap(res);

      if (!cancelled) {
        setProfileFighter(data);
      }
    } catch (err) {
      console.error("Fighter profile load error:", err);

      if (!cancelled) {
        setProfileFighter(null);
      }
    }
  }

  loadFighterProfile();

  return () => {
    cancelled = true;
  };
}, [
  adminEditMode,
  adminEditFighterId,
  isClubCoach,
  selectedFighterId,
  user?.fighter_id,
]);

  // =======================================================
  // RESET AFTER CATEGORY CHANGE
  // =======================================================

  useEffect(() => {
    setSelectedWeight("");
  }, [category]);


  // =======================================================
  // CURRENT WEIGHTS
  // =======================================================

  const currentWeights =
    category === "main"
      ? weightGroups.main
      : category === "altan_garaa"
      ? weightGroups.altan_garaa
      : [];

      useEffect(() => {
  if (!adminEditMode || !existingRegistration) return;
  if (!currentWeights?.length) return;

  const existingWeightId =
    existingRegistration.division_weightclass_id;

  if (!existingWeightId) return;

  const found = currentWeights.some(
    (w) =>
      Number(getWeightId(w)) === Number(existingWeightId)
  );

  if (found) {
    setSelectedWeight(String(existingWeightId));
  }
}, [
  adminEditMode,
  existingRegistration,
  currentWeights,
]);

  // =======================================================
  // REGISTER
  // =======================================================

  const submit = async () => {
    setError("");
    setSuccess(null);

    // -----------------------------------------------------
    // LOGIN CHECK
    // -----------------------------------------------------

    if (!canAccessRegistration) {
  setError(
    "Fighter account-аараа нэвтэрсэн байх шаардлагатай."
  );
  return;
}

    // -----------------------------------------------------
// DETERMINE FIGHTER
// -----------------------------------------------------

const fighterId = Number(
  adminEditMode
    ? adminEditFighterId
    : isClubCoach
      ? selectedFighterId
      : user?.fighter_id
);

if (!fighterId) {
  setError(
    adminEditMode
      ? "Тамирчны ID олдсонгүй."
      : isClubCoach
        ? "Тамирчнаа сонгоно уу."
        : "Fighter account-аараа нэвтэрсэн байх шаардлагатай."
  );

  return;
}

    // -----------------------------------------------------
    // FORM VALIDATION
    // -----------------------------------------------------

    if (!category) {
      setError(
        "Үндсэн эсвэл Алтан гараа сонгоно уу."
      );

      return;
    }

    if (!gender) {
      setError(
        "Хүйсээ сонгоно уу."
      );

      return;
    }

    if (!ageGroup) {
  setError(
    "Насны ангиллаа сонгоно уу."
  );

  return;
}

// -----------------------------------------------------
// AGE DIVISION VALIDATION
// -----------------------------------------------------

const selectedDivision =
  ageDivisions.find(
    (division) =>
      String(getAgeDivisionId(division)) ===
      String(ageGroup)
  );

if (!selectedDivision) {
  setError(
    "Сонгосон насны ангилал буруу байна."
  );

  return;
}

if (
  !adminEditMode &&
  !divisionCanBeSelectedByAge(
    selectedDivision,
    fighterAge
  )
) {
  setError(
    "Энэ насны ангилалд бүртгүүлэх боломжгүй."
  );

  return;
}

if (!selectedWeight) {
  setError(
    "Жингийн ангиллаа сонгоно уу."
  );

  return;
}

    // -----------------------------------------------------
    // EXISTING REGISTRATION
    // -----------------------------------------------------

    if (!adminEditMode && existingRegistration) {
  setError(
    "Энэ тамирчин энэ тэмцээнд аль хэдийн бүртгүүлсэн байна."
  );
  return;
}

if (adminEditMode) {
  const registrationId =
    existingRegistration?.registration_id ??
    existingRegistration?.id;

  if (!registrationId) {
    setError("Бүртгэлийн ID олдсонгүй.");
    return;
  }

  try {
    setSubmitting(true);
    setError("");

    const res = await API.patch(
      `/tournaments/${tournamentId}/registration/${registrationId}/category`,
      {
        division_id: Number(ageGroup),
        division_weightclass_id: Number(selectedWeight),
        gender,
      }
    );

    const data = unwrap(res);

    setSuccess({
      registration_id:
        data?.registration_id ??
        data?.registration?.registration_id ??
        registrationId,
      message:
        "Бүртгэлийн ангилал амжилттай шинэчлэгдлээ.",
    });

    // Refresh registration data
    const registrationRes = await API.get(
      `/tournaments/${tournamentId}/registration`
    );

    const registrations = normalizeArray(
      unwrap(registrationRes),
      ["registrations"]
    );

    const updated = registrations.find(
      (registration) =>
        Number(registration.fighter_id) === Number(fighterId)
    );

    if (updated) {
      setExistingRegistration(updated);
    }

  } catch (err) {
    console.error(
      "Admin registration update failed:",
      err
    );

    setError(
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      "Бүртгэлийн ангилал шинэчлэхэд алдаа гарлаа."
    );
  } finally {
    setSubmitting(false);
  }

  return;
}

    try {
      setSubmitting(true);

      // ---------------------------------------------------
      // IMPORTANT:
      // Send fighter_id to backend.
      // ---------------------------------------------------

      const res = await API.post(
        `/tournaments/${tournamentId}/register`,
        {
          fighter_id: fighterId,

          division_id:
            Number(ageGroup),

          division_weightclass_id:
            Number(selectedWeight),

          gender,

          type: category,
        }
      );

      const data = unwrap(res);

      // ---------------------------------------------------
      // SUCCESS
      // ---------------------------------------------------

      setSuccess({
        registration_id:
          data?.registration_id ??
          data?.id ??
          null,

        message:
          "Амжилттай бүртгэгдлээ.",
      });

      // ---------------------------------------------------
      // REFRESH REGISTRATION
      // ---------------------------------------------------

      try {
        const registrationRes =
          await API.get(
            `/tournaments/${tournamentId}/registration`
          );

        const registrations =
          normalizeArray(
            unwrap(
              registrationRes
            ),
            ["registrations"]
          );

        const mine =
          registrations.find(
            (registration) =>
              Number(
                registration.fighter_id
              ) ===
              Number(fighterId)
          );

        setExistingRegistration(
          mine || {
            registration_id:
              data?.registration_id ??
              data?.id,

            fighter_id:
              fighterId,

            division_id:
              Number(ageGroup),

            division_weightclass_id:
              Number(selectedWeight),

            registration_gender:
              gender,

            type: category,

            approved: false,

            paid: false,

            weighed_in: false,
          }
        );
      } catch {
        setExistingRegistration({
          registration_id:
            data?.registration_id ??
            data?.id,

          fighter_id:
            fighterId,

          division_id:
            Number(ageGroup),

          division_weightclass_id:
            Number(selectedWeight),

          registration_gender:
            gender,

          type: category,

          approved: false,

          paid: false,

          weighed_in: false,
        });
      }
    } catch (err) {
      console.error(
        "Registration failed:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Тэмцээнд бүртгүүлэхэд алдаа гарлаа."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // =======================================================
// ADMIN EDIT — LOAD EXISTING REGISTRATION INTO FORM
// =======================================================

useEffect(() => {
  if (!adminEditMode) return;
  if (!existingRegistration) return;

  // -----------------------------
  // GENDER
  // -----------------------------
  const registeredGender =
    existingRegistration.registration_gender ||
    existingRegistration.gender ||
    "";

  if (registeredGender) {
    setGender(String(registeredGender).toLowerCase());
  }

  // -----------------------------
  // AGE DIVISION
  // -----------------------------
  const registeredDivision =
    existingRegistration.division_id;

  if (registeredDivision != null) {
    setAgeGroup(String(registeredDivision));
  }

  // -----------------------------
  // CATEGORY
  // -----------------------------
  const registeredType =
    existingRegistration.type ||
    existingRegistration.weightclass_type ||
    "";

  if (
    registeredType === "altan_garaa" ||
    registeredType === "main"
  ) {
    setCategory(registeredType);
  }
}, [
  adminEditMode,
  existingRegistration,
]);

  // =======================================================
  // UNREGISTER
  // =======================================================

  const unregister = async () => {
    if (!existingRegistration) {
      return;
    }

    const registrationId =
      existingRegistration.registration_id ??
      existingRegistration.id;

    if (!registrationId) {
      setError(
        "Бүртгэлийн ID олдсонгүй."
      );

      return;
    }

    const confirmed =
      window.confirm(
        "Та энэ тамирчны тэмцээний бүртгэлийг цуцлахдаа итгэлтэй байна уу?"
      );

    if (!confirmed) {
      return;
    }

    try {
      setUnregistering(true);

      setError("");

      setSuccess(null);

      await API.delete(
        `/tournaments/${tournamentId}/registration/${registrationId}`
      );

      // ---------------------------------------------------
      // CLEAR REGISTRATION
      // ---------------------------------------------------

      setExistingRegistration(null);

      // ---------------------------------------------------
      // CLEAR FORM
      // ---------------------------------------------------

      setCategory("");

      setAgeGroup("");

      setSelectedWeight("");

      setWeightGroups({
        main: [],
        altan_garaa: [],
      });

      // ---------------------------------------------------
      // COACH:
      // Keep selected fighter so they can easily re-register.
      //
      // NORMAL FIGHTER:
      // Keep his own fighter selected internally.
      // ---------------------------------------------------

      setSuccess({
        message:
          "Бүртгэл амжилттай цуцлагдлаа. Та дахин бүртгүүлж болно.",
      });
    } catch (err) {
      console.error(
        "Unregister failed:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Бүртгэл цуцлахад алдаа гарлаа."
      );
    } finally {
      setUnregistering(false);
    }
  };

  // =======================================================
  // LOADING
  // =======================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-yellow-400 text-xs font-black uppercase tracking-widest animate-pulse">
          Тэмцээний мэдээлэл ачааллаж байна...
        </div>
      </div>
    );
  }

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-20 px-4">
      <div className="max-w-xl mx-auto">

        {/* ================================================= */}
        {/* BACK */}
        {/* ================================================= */}

        <Link
          to="/tournaments"
          className="inline-block text-neutral-500 hover:text-white text-xs font-bold uppercase mb-5"
        >
          ← Тэмцээнүүд
        </Link>

        <div className="bg-neutral-950 border border-neutral-800 rounded-2xl p-6 shadow-2xl">

          {/* ================================================= */}
          {/* LOGIN REQUIRED */}
          {/* ================================================= */}

          {!canAccessRegistration ? (

            <div className="rounded-xl border border-red-800/50 bg-red-950/20 p-5">

              <div className="text-red-400 font-black uppercase text-sm">
                Fighter account шаардлагатай
              </div>

              <div className="text-neutral-400 text-xs mt-2">
                Тэмцээнд бүртгүүлэхийн тулд fighter
                account-аараа нэвтэрнэ үү.
              </div>

              <Link
                to="/login"
                className="inline-block mt-4 bg-yellow-500 text-black px-5 py-3 rounded-xl text-xs font-black uppercase"
              >
                Нэвтрэх
              </Link>

            </div>

          ) : existingRegistration && !adminEditMode ? (

            // =================================================
            // EXISTING REGISTRATION / REGISTRATION DASHBOARD
            // =================================================

            <div className="space-y-5">

              {/* HERO */}
              <div className="relative overflow-hidden rounded-2xl border border-yellow-500/20 bg-gradient-to-br from-yellow-500/10 via-neutral-950 to-neutral-950 p-6">
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-yellow-500/10 blur-3xl" />

                <div className="relative">
                  <div className="flex items-center gap-2 text-emerald-400 text-[10px] font-black uppercase tracking-[0.2em]">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
                      ✓
                    </span>
                    БҮРТГЭЛ БАТАЛГААЖЛАА
                  </div>

                  <h2 className="mt-3 text-2xl md:text-3xl font-black uppercase italic leading-tight">
                    ТА АМЖИЛТТАЙ БҮРТГҮҮЛЛЭЭ
                  </h2>

                  <p className="mt-2 text-sm text-neutral-400">
                     Таны тэмцээний бүртгэл идэвхтэй байна. Тэмцээний мэдээлэл болон
  бүртгэлийн явцаа энэ хуудсаар хянах боломжтой.
                  </p>
                </div>
              </div>

              {/* FIGHTER */}
              <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-5">
                <div className="text-neutral-500 text-[9px] font-black uppercase tracking-[0.2em]">
                  БҮРТГҮҮЛСЭН ТАМИРЧИН
                </div>

                <div className="mt-4 flex items-center gap-4">
                  {(() => {
                    const fighterImage = isClubCoach
                      ? selectedFighter?.image_url
                      : existingRegistration?.fighter_image ||
                        existingRegistration?.image_url ||
                        user?.image_url;

                    const imageSrc = fighterImage
                      ? fighterImage.startsWith("http")
                        ? fighterImage
                        : `${API_BASE_URL}${fighterImage}`
                      : null;

                    const fighterName = isClubCoach
                      ? selectedFighter
                        ? `${selectedFighter.name} ${selectedFighter.surname}`
                        : existingRegistration.fighter_name ||
                          "Registered Fighter"
                      : existingRegistration?.name &&
                        existingRegistration?.surname
                      ? `${existingRegistration.name} ${existingRegistration.surname}`
                      : user?.name ||
                        user?.fighter_name ||
                        "Your Fighter";

                    return (
                      <>
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt=""
                            className="h-16 w-16 rounded-full object-cover border-2 border-yellow-500/40"
                          />
                        ) : (
                          <div className="h-16 w-16 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center text-yellow-400 text-xl font-black">
                            {fighterName?.charAt(0) || "F"}
                          </div>
                        )}

                        <div className="min-w-0">
                          <div className="text-lg font-black uppercase truncate">
                            {fighterName}
                          </div>

                          {(isClubCoach
                            ? coachClub?.club_name
                            : existingRegistration?.club_name ||
                              user?.club_name) && (
                            <div className="text-xs text-neutral-500 mt-1 uppercase">
                              {isClubCoach
                                ? coachClub.club_name
                                : existingRegistration.club_name ||
                                  user.club_name}
                            </div>
                          )}

                          {isClubCoach && (
                            <div className="mt-2 inline-flex rounded-full bg-yellow-500/10 border border-yellow-500/20 px-2.5 py-1 text-[8px] font-black uppercase tracking-widest text-yellow-400">
                              Registered by Club Coach
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* PAYMENT / APPROVAL */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div
                  className={`rounded-2xl border p-5 ${
                    existingRegistration.paid
                      ? "border-emerald-500/30 bg-emerald-950/20"
                      : "border-amber-500/30 bg-amber-950/20"
                  }`}
                >
                  <div className="text-[9px] font-black uppercase tracking-[0.18em] text-neutral-500">
  Төлбөрийн төлөв
</div>

<div
  className={`mt-2 text-2xl font-black uppercase ${
    existingRegistration.paid
      ? "text-emerald-400"
      : "text-amber-400"
  }`}
>
  {existingRegistration.paid
    ? "✓ ТӨЛӨГДСӨН"
    : "⚠ ТӨЛБӨР ХҮЛЭЭГДЭЖ БАЙНА"}
</div>

<div className="mt-1 text-[10px] text-neutral-500">
  {existingRegistration.paid
    ? "Бүртгэлийн төлбөр баталгаажсан."
    : "Бүртгэлийн төлбөрийг хийж баталгаажуулна уу."}
</div>

{!existingRegistration.paid && (
  <button
    onClick={() => {
      document
        .getElementById("payment-information")
        ?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    }}
    className="mt-4 flex items-center justify-center w-full py-2.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black text-[10px] font-black uppercase transition"
  >
    ТӨЛБӨРИЙН МЭДЭЭЛЭЛ ↓
  </button>
)}
                </div>

                <div
                  className={`rounded-2xl border p-5 ${
                    existingRegistration.approved
                      ? "border-emerald-500/30 bg-emerald-950/20"
                      : "border-neutral-800 bg-neutral-950"
                  }`}
                >
                  <div className="text-[9px] font-black uppercase tracking-[0.18em] text-neutral-500">
  Баталгаажуулалтын төлөв
</div>

<div
  className={`mt-2 text-2xl font-black uppercase ${
    existingRegistration.approved
      ? "text-emerald-400"
      : "text-yellow-400"
  }`}
>
  {existingRegistration.approved
    ? "✓ БАТАЛГААЖСАН"
    : "ЖИН ҮЗЛЭГ ХҮЛЭЭГДЭЖ БАЙНА"}
</div>
 
<div className="mt-1 text-[10px] text-neutral-500">
  {existingRegistration.approved
    ? "Таны тэмцээний бүртгэл баталгаажсан."
    : "Албан ёсны жин үзлэгт амжилттай орсны дараа таны бүртгэл баталгаажна."}
</div>
                </div>
              </div>

              {/* COMPETITION CATEGORY */}
              <div className="rounded-2xl border border-yellow-500/20 bg-neutral-950 overflow-hidden">
                <div className="px-5 py-4 border-b border-neutral-800">
                  <div className="text-yellow-400 text-[9px] font-black uppercase tracking-[0.2em]">
                    ТАНЫ ТЭМЦЭЭНИЙ АНГИЛАЛ
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4">
                  <div className="p-4 border-b md:border-b-0 md:border-r border-neutral-800">
                    <div className="text-[8px] text-neutral-600 uppercase font-black">
                      Ангилал
                    </div>
                    <div className="mt-1 text-sm font-black uppercase">
                      {getRegistrationCategory(existingRegistration)}
                    </div>
                  </div>

                  <div className="p-4 border-b md:border-b-0 md:border-r border-neutral-800">
                    <div className="text-[8px] text-neutral-600 uppercase font-black">
                      Насны ангилал
                    </div>
                    <div className="mt-1 text-sm font-black uppercase">
                      {getRegistrationAge(existingRegistration)}
                    </div>
                  </div>

                  <div className="p-4 border-b md:border-b-0 md:border-r border-neutral-800">
                    <div className="text-[8px] text-neutral-600 uppercase font-black">
                      Хүйс
                    </div>
                    <div className="mt-1 text-sm font-black uppercase">
                      {getRegistrationGender(existingRegistration)}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="text-[8px] text-neutral-600 uppercase font-black">
                      Жин
                    </div>
                    <div className="mt-1 text-base font-black uppercase text-yellow-400">
                      {getRegistrationWeight(existingRegistration)}
                    </div>
                  </div>
                </div>
              </div>

              {/* PROGRESS */}
              <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-5">
                <div className="text-neutral-500 text-[9px] font-black uppercase tracking-[0.2em]">
                  Registration Progress
                </div>

                <div className="mt-5 grid grid-cols-4 gap-2">
                  {[
                    { label: "Registered", done: true },
                    { label: "Paid", done: !!existingRegistration.paid },
                    { label: "Approved", done: !!existingRegistration.approved },
                    { label: "Weigh-in", done: !!existingRegistration.weighed_in },
                  ].map((step, index) => (
                    <div key={step.label} className="relative text-center">
                      {index < 3 && (
                        <div
                          className={`absolute left-1/2 top-3 h-px w-full ${
                            step.done &&
                            [
                              existingRegistration.paid,
                              existingRegistration.approved,
                              existingRegistration.weighed_in,
                            ][index]
                              ? "bg-emerald-500/60"
                              : "bg-neutral-800"
                          }`}
                        />
                      )}

                      <div
                        className={`relative mx-auto h-7 w-7 rounded-full flex items-center justify-center text-[9px] font-black border ${
                          step.done
                            ? "bg-emerald-500 text-black border-emerald-400"
                            : "bg-neutral-900 text-neutral-600 border-neutral-800"
                        }`}
                      >
                        {step.done ? "✓" : index + 1}
                      </div>

                      <div
                        className={`mt-2 text-[7px] sm:text-[8px] font-black uppercase ${
                          step.done ? "text-emerald-400" : "text-neutral-600"
                        }`}
                      >
                        {step.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

{/* ================================================= */}
{/* PAYMENT INFORMATION */}
{/* ================================================= */}

{!existingRegistration.paid && (
  <div
    id="payment-information"
    className="rounded-2xl overflow-hidden border border-yellow-500/30 bg-neutral-950"
  >
    
    {/* HEADER */}
    <div className="px-5 py-4 border-b border-yellow-500/20 bg-gradient-to-r from-yellow-500/10 to-transparent">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-full bg-yellow-500 text-black flex items-center justify-center font-black">
          ₮
        </div>

        <div>
          <div className="text-yellow-400 text-[9px] font-black uppercase tracking-[0.2em]">
            ТӨЛБӨРИЙН МЭДЭЭЛЭЛ
          </div>
          <div className="text-[10px] text-neutral-500 mt-0.5">
            Бүртгэлээ баталгаажуулахын тулд төлбөрөө хийнэ үү
          </div>
        </div>
      </div>
    </div>


    <div className="p-5 space-y-5">

      {/* PRICE */}
<div className="rounded-2xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/10 via-neutral-950 to-neutral-950 p-5">

  <div className="text-[9px] font-black uppercase tracking-[0.2em] text-neutral-500">
    БҮРТГЭЛИЙН ТӨЛБӨР
  </div>

  <div className="mt-2 flex items-end gap-2">
    <div className="text-4xl font-black tracking-tight text-yellow-400">
      95,000₮
    </div>

    <div className="pb-1 text-[9px] font-bold uppercase tracking-wider text-neutral-500">
      / 1 тамирчин
    </div>
  </div>

  <div className="mt-2 text-[10px] leading-relaxed text-neutral-400">
    Тэмцээнд оролцох бүртгэлийн үндсэн төлбөр.
  </div>


      </div>


      {/* BANK */}
      <div className="rounded-xl border border-neutral-800 bg-neutral-900/50 overflow-hidden">

        <div className="px-4 py-3 border-b border-neutral-800">
          <div className="text-[9px] font-black uppercase tracking-widest text-neutral-500">
            ДАНСНЫ МЭДЭЭЛЭЛ
          </div>
        </div>

        <div className="p-4 space-y-4">

          <div>
            <div className="text-[8px] text-neutral-600 uppercase font-black">
              БАНК
            </div>

            <div className="mt-1 text-sm font-bold text-white">
              Khan Bank
            </div>

            <div className="text-xs text-neutral-500">
              Эзэмшигч: Э. Мөнхбаатар
            </div>
          </div>


          {/* ACCOUNT */}
          <div className="flex items-center justify-between gap-3 rounded-lg bg-black/40 border border-neutral-800 p-3">

            <div>
              <div className="text-[8px] text-neutral-600 font-black uppercase">
                ДАНСНЫ ДУГААР
              </div>

              <div className="text-lg font-black text-yellow-400 tracking-wide">
                5079090204
              </div>
            </div>

            <button
  onClick={() => copyText("5079090204")}
  className={`shrink-0 px-3 py-2 text-[9px] rounded-lg font-black transition ${
    copiedText === "5079090204"
      ? "bg-emerald-500 text-black"
      : "bg-yellow-500 text-black hover:bg-yellow-400"
  }`}
>
  {copiedText === "5079090204" ? "✓ ХУУЛАГДЛАА" : "ХУУЛАХ"}
</button>

          </div>


          {/* IBAN */}
          <div className="flex items-center justify-between gap-3 rounded-lg bg-black/40 border border-neutral-800 p-3">

            <div>
              <div className="text-[8px] text-neutral-600 font-black uppercase">
                IBAN
              </div>

              <div className="text-base font-black text-white tracking-wide">
                29000500
              </div>
            </div>

            <button
  onClick={() => copyText("29000500")}
  className={`shrink-0 px-3 py-2 text-[9px] rounded-lg font-black transition ${
    copiedText === "29000500"
      ? "bg-emerald-500 text-black"
      : "bg-neutral-800 border border-neutral-700 text-white hover:border-yellow-500"
  }`}
>
  {copiedText === "29000500" ? "✓ ХУУЛАГДЛАА" : "ХУУЛАХ"}
</button>

          </div>

        </div>
      </div>


      {/* TRANSACTION DESCRIPTION */}
<div className="rounded-2xl border border-yellow-500/40 bg-yellow-500/[0.08] p-5">

  <div className="text-[10px] text-yellow-400 font-black uppercase tracking-[0.2em]">
    ГҮЙЛГЭЭНИЙ УТГА
  </div>

  <div className="mt-3 text-2xl md:text-3xl font-black text-white leading-tight">
    WAR – Нэр – Утас
  </div>

  <div className="mt-3 text-xs md:text-sm font-semibold text-yellow-400">
    Жишээ: WAR M. Shagai 88412233 
  </div>

  <div className="mt-3 text-[10px] md:text-xs leading-relaxed text-neutral-400">
  <span className="text-white font-bold">WAR + Нэр + (WARFC-д бүртгэлтэй утасны
          дугаар)</span>-аа бичнэ үү.
  </div>

</div>


      {/* NOTICE */}
      <div className="rounded-xl bg-neutral-900/50 border border-neutral-800 p-4">

        <div className="flex gap-3">
          <div className="text-yellow-400 font-black">
            !
          </div>

          <div className="text-[10px] leading-relaxed text-neutral-400">
            Гүйлгээний утгыг буруу бичсэн тохиолдолд
            <span className="text-white font-bold"> WARFC page </span>
            руу гүйлгээний баримтаа илгээж бүртгэлээ баталгаажуулна уу.
            Төлбөр баталгаажсаны дараа таны бүртгэлийн төлөв
            <span className="text-emerald-400 font-bold"> “ТӨЛӨГДСӨН” </span>
            болж шинэчлэгдэнэ.
          </div>
        </div>

      </div>

    </div>

  </div>
)}

              {/* IMPORTANT */}
              <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-5">
                <div className="text-yellow-400 text-[9px] font-black uppercase tracking-[0.2em]">
                  Important
                </div>

                <div className="mt-3 space-y-2 text-[10px] leading-relaxed text-neutral-400">
                  <div>• Тэмцээний өмнө албан ёсны хуваарь, мэдээллийг шалгана уу.</div>
<div>• Жин үзүүлэлт болон бүртгэлд цагтаа ирнэ үү.</div>
<div>• Таны бүртгүүлсэн жин албан ёсны жин үзүүлэлттэй тохирсон байх ёстой.</div>
<div>• WAR FC-ийн тэмцээний дүрэм, шаардлагыг мөрдөнө үү.</div>
                </div>
              </div>

              {/* ACTIONS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link
                  to="/tournaments"
                  className="py-4 rounded-xl border border-neutral-700 bg-neutral-900 hover:border-yellow-500 text-center text-xs font-black uppercase transition"
                >
                  ← Tournament List
                </Link>

                <Link
                  to="/profile"
                  className="py-4 rounded-xl bg-yellow-500 hover:bg-yellow-400 text-center text-black text-xs font-black uppercase transition"
                >
                  Fighter Profile
                </Link>
              </div>

              {/* UNREGISTER */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={unregister}
                  disabled={unregistering}
                  className="w-full py-3 rounded-xl border border-red-900/70 bg-red-950/20 hover:bg-red-950/40 disabled:opacity-40 text-red-500 font-black uppercase text-[10px] transition"
                >
                  {unregistering ? "Processing..." : "БҮРТГЭЛ ЦУЦЛАХ"}
                </button>

                <div className="text-[10px] text-yellow-500/80 mt-3 text-center leading-relaxed font-bold">
  ⚠ Ангилал эсвэл жингийн ангиллаа өөрчлөх бол бүртгэлээ цуцалж,
  дахин бүртгүүлнэ үү.
</div>
              </div>

            </div>

          ) : (

            // =================================================
            // REGISTRATION WIZARD
            // =================================================

            <>

              {/* ================================================= */}
              {/* COACH FIGHTER SELECTION */}
              {/* ================================================= */}

              {isClubCoach && (
  <div className="mb-7">

    <div className="flex items-center gap-3 mb-3">
      <div className="w-7 h-7 rounded-full bg-yellow-500 text-black flex items-center justify-center text-xs font-black">
        3
      </div>

      <div>
        <div className="text-[10px] text-yellow-400 font-black uppercase tracking-widest">
          Step 3
        </div>

        <div className="font-black uppercase">
          Select Fighter
        </div>
      </div>
    </div>

    {/* SEARCH */}
    <div className="relative mb-3">
      <input
        type="text"
        value={fighterSearch}
        onChange={(e) =>
          setFighterSearch(e.target.value)
        }
        placeholder="Search fighter..."
        className="
          w-full
          bg-neutral-900
          border border-neutral-700
          rounded-xl
          px-4 py-3
          text-sm
          text-white
          placeholder:text-neutral-600
          focus:outline-none
          focus:border-yellow-500
        "
      />
    </div>

    {/* FIGHTERS */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {filteredCoachFighters.map((fighter) => {
        const selected =
          String(selectedFighterId) ===
          String(fighter.fighter_id);

        return (
          <button
            key={fighter.fighter_id}
            type="button"
            onClick={() => {
  setSelectedFighterId(
    String(fighter.fighter_id)
  );

  setFighterSearch("");

  setTimeout(() => {
    registrationOptionsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, 100);
}}
            className={`
              text-left
              p-4
              rounded-xl
              border
              transition
              ${
                selected
                  ? "bg-yellow-500 text-black border-yellow-400"
                  : "bg-neutral-900 border-neutral-700 hover:border-yellow-500"
              }
            `}
          >
            <div className="font-black">
              {fighter.name} {fighter.surname}
            </div>

            {fighter.nickname && (
              <div
                className={`text-xs mt-1 ${
                  selected
                    ? "text-black/60"
                    : "text-neutral-500"
                }`}
              >
                "{fighter.nickname}"
              </div>
            )}
          </button>
        );
      })}
    </div>

    {filteredCoachFighters.length === 0 && (
      <div className="p-5 rounded-xl bg-neutral-900 border border-neutral-800 text-center text-neutral-500 text-sm">
        No fighters found.
      </div>
    )}

  </div>
)}

              {/* ================================================= */}
              {/* REGISTERING FIGHTER */}
              {/* ================================================= */}

              <div className="mb-6 p-4 rounded-xl bg-neutral-900 border border-neutral-800">

                <div className="text-neutral-500 text-[9px] uppercase font-black">
                  Registering Fighter
                </div>

                <div className="text-white font-black uppercase mt-1">

                  {isClubCoach
                    ? selectedFighter
                      ? `${selectedFighter.name} ${selectedFighter.surname}`
                      : "Select a fighter"
                    : user.name ||
                      user.fighter_name ||
                      "Your Fighter"}

                </div>

                {(isClubCoach
                  ? coachClub?.club_name
                  : user.club_name) && (

                  <div className="text-neutral-500 text-xs mt-1">
                    {isClubCoach
                      ? coachClub.club_name
                      : user.club_name}
                  </div>

                )}

                {isClubCoach &&
                  selectedFighter && (

                    <div className="mt-2 text-yellow-400 text-[9px] font-black uppercase">
                      Registered by Club Coach
                    </div>

                  )}

              </div>

              {/* ================================================= */}
              {/* ERROR */}
              {/* ================================================= */}

              {error && (

                <div className="mb-5 p-4 rounded-xl bg-red-950/30 border border-red-800/50 text-red-400 text-xs font-bold">
                  {error}
                </div>

              )}

              {/* ================================================= */}
              {/* SUCCESS */}
              {/* ================================================= */}

              {success && (

                <div className="mb-5 p-5 rounded-xl bg-emerald-950/30 border border-emerald-700/50">

                  <div className="text-emerald-400 font-black uppercase">
                    ✓{" "}
                    {success.message ||
                      "Successfully Registered"}
                  </div>

                  <div className="text-neutral-400 text-xs mt-2">
                    Your registration details are now available below.
                  </div>

                </div>

              )}

<div ref={registrationOptionsRef}>
              {/* ================================================= */}
              {/* STEP 1 - CATEGORY */}
              {/* ================================================= */}

              <div className="mb-7">

                <div className="flex items-center gap-3 mb-3">

                  <div className="w-7 h-7 rounded-full bg-yellow-500 text-black flex items-center justify-center text-xs font-black">
                    {isClubCoach
                      ? "2"
                      : "1"}
                  </div>

                  <div>

                    <div className="text-[10px] text-yellow-400 font-black uppercase tracking-widest">
                      Step{" "}
                      {isClubCoach
                        ? "2"
                        : "1"}
                    </div>

                    <div className="font-black uppercase">
                      Ангилал сонгох
                    </div>

                  </div>

                </div>

                <div className="grid grid-cols-2 gap-3">

                  {/* MAIN */}

                  <button
                    type="button"
                    onClick={() =>
                      setCategory("main")
                    }
                    className={`p-5 rounded-xl border transition ${
                      category === "main"
                        ? "bg-yellow-500 text-black border-yellow-400"
                        : "bg-neutral-900 border-neutral-700 hover:border-yellow-500"
                    }`}
                  >

                    <div className="text-lg font-black uppercase">
                      Үндсэн
                    </div>

                    <div
                      className={`text-[9px] mt-1 font-bold uppercase ${
                        category === "main"
                          ? "text-black/60"
                          : "text-neutral-500"
                      }`}
                    >
                      Main
                    </div>

                  </button>

                  {/* ALTAN GARAA */}

                  <button
                    type="button"
                    onClick={() =>
                      setCategory(
                        "altan_garaa"
                      )
                    }
                    className={`p-5 rounded-xl border transition ${
                      category ===
                      "altan_garaa"
                        ? "bg-yellow-500 text-black border-yellow-400"
                        : "bg-neutral-900 border-neutral-700 hover:border-yellow-500"
                    }`}
                  >

                    <div className="text-lg font-black uppercase">
                      Алтан гараа
                    </div>

                    <div
                      className={`text-[9px] mt-1 font-bold uppercase ${
                        category ===
                        "altan_garaa"
                          ? "text-black/60"
                          : "text-neutral-500"
                      }`}
                    >
                      Altan Garaa
                    </div>

                  </button>

                </div>

              </div>

              {/* ================================================= */}
{/* FIGHTER ELIGIBILITY */}
{/* ================================================= */}

{category && registeringFighter && (

  <div className="mb-7">

    <div className="rounded-2xl border border-yellow-500/20 bg-gradient-to-br from-yellow-500/10 via-neutral-950 to-neutral-950 p-5">

      <div className="text-yellow-400 text-[9px] font-black uppercase tracking-[0.2em]">
        Fighter Eligibility
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">

        {/* AGE */}

        <div className="rounded-xl bg-neutral-900 border border-neutral-800 p-4">

          <div className="text-[8px] text-neutral-600 uppercase font-black">
            Competition Age
          </div>

          <div className="mt-1 text-2xl font-black text-white">
            {fighterAge != null
              ? `${fighterAge} YEARS`
              : "—"}
          </div>

        </div>

        {/* GENDER */}

        <div className="rounded-xl bg-neutral-900 border border-neutral-800 p-4">

          <div className="text-[8px] text-neutral-600 uppercase font-black">
            Gender
          </div>

          <div className="mt-1 text-2xl font-black text-yellow-400 uppercase">
            {gender || "—"}
          </div>

        </div>

      </div>

      <div className="mt-4 flex items-start gap-3">

        <div className="text-emerald-400 text-lg">
          ✓
        </div>

        <div className="text-[9px] leading-relaxed text-neutral-400">
          Age and gender are automatically determined
          from the fighter profile and cannot be manually
          changed during tournament registration.
        </div>

      </div>

    </div>

  </div>

)}

              {/* ================================================= */}
              {/* STEP 3 - AGE */}
              {/* ================================================= */}

              {category &&
                gender && (

                  <div className="mb-7">

                    <div className="flex items-center gap-3 mb-3">

                      <div className="w-7 h-7 rounded-full bg-yellow-500 text-black flex items-center justify-center text-xs font-black">
                        {isClubCoach
                          ? "4"
                          : "3"}
                      </div>

                      <div>

                        <div className="text-[10px] text-yellow-400 font-black uppercase tracking-widest">
                          Step{" "}
                          {isClubCoach
                            ? "4"
                            : "3"}
                        </div>

                        <div className="font-black uppercase">
                          Насны ангилал
                        </div>

                      </div>

                    </div>

                    {loadingAges ? (

                      <div className="p-4 rounded-xl bg-neutral-900 border border-neutral-800 text-yellow-400 text-xs font-bold">
                        Loading age divisions...
                      </div>

                    ) : (

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">

                        {eligibleAgeDivisions.map(
  (division) => {

                            const id =
                              getAgeDivisionId(
                                division
                              );

                            const selected =
                              String(id) ===
                              String(
                                ageGroup
                              );

                            return (

                              <button
  key={id}
  type="button"
  onClick={() =>
    setAgeGroup(String(id))
  }
  className={`p-4 rounded-xl border font-black text-sm transition ${
    selected
      ? "bg-yellow-500 text-black border-yellow-400"
      : "bg-neutral-900 border-neutral-700 hover:border-yellow-500"
  }`}
>
  <div>
    {getAgeDivisionName(division)}
  </div>

  {divisionContainsAge(
    division,
    fighterAge
  ) && (
    <div
      className={`mt-1 text-[8px] uppercase tracking-widest ${
        selected
          ? "text-black/60"
          : "text-emerald-400"
      }`}
    >
      Recommended
    </div>
  )}
</button>

                            );
                          }
                        )}

                      </div>

                    )}

                    {!loadingAges &&
                      ageDivisions.length ===
                        0 && (

                        <div className="text-red-400 text-[10px] font-bold mt-3">
                          Энэ тэмцээнд насны
                          ангилал бүртгэгдээгүй
                          байна.
                        </div>

                      )}

                  </div>

                )}

              {/* ================================================= */}
              {/* STEP 4 - WEIGHT */}
              {/* ================================================= */}

              {category &&
                gender &&
                ageGroup && (

                  <div className="mb-7">

                    <div className="flex items-center gap-3 mb-3">

                      <div className="w-7 h-7 rounded-full bg-yellow-500 text-black flex items-center justify-center text-xs font-black">
                        {isClubCoach
                          ? "5"
                          : "4"}
                      </div>

                      <div>

                        <div className="text-[10px] text-yellow-400 font-black uppercase tracking-widest">
                          Step{" "}
                          {isClubCoach
                            ? "5"
                            : "4"}
                        </div>

                        <div className="font-black uppercase">
                          Жингийн ангилал
                        </div>

                      </div>

                    </div>

                    {loadingWeights ? (

                      <div className="p-4 rounded-xl bg-neutral-900 border border-neutral-800 text-yellow-400 text-xs font-bold animate-pulse">
                        Loading weight classes...
                      </div>

                    ) : currentWeights.length ===
                      0 ? (

                      <div className="p-5 rounded-xl bg-red-950/20 border border-red-800/40 text-red-400 text-xs font-bold">
                        Энэ сонголтод жингийн
                        ангилал олдсонгүй.
                      </div>

                    ) : (

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">

                        {currentWeights.map(
                          (weight) => {

                            const id =
                              getWeightId(
                                weight
                              );

                            const selected =
                              String(id) ===
                              String(
                                selectedWeight
                              );

                            return (

                              <button
                                key={id}
                                type="button"
                                onClick={() =>
                                  setSelectedWeight(
                                    String(id)
                                  )
                                }
                                className={`p-4 rounded-xl border transition ${
                                  selected
                                    ? "bg-yellow-500 text-black border-yellow-400"
                                    : "bg-neutral-900 border-neutral-700 hover:border-yellow-500"
                                }`}
                              >

                                <div className="font-black text-sm">
                                  {getWeightName(
                                    weight
                                  )}
                                </div>

                                {weight.weight !=
                                  null && (

                                  <div
                                    className={`text-[9px] mt-1 font-bold ${
                                      selected
                                        ? "text-black/60"
                                        : "text-neutral-500"
                                    }`}
                                  >
                                    {weight.weight} kg
                                  </div>

                                )}

                              </button>

                            );
                          }
                        )}

                      </div>

                    )}

                  </div>

                )}

              {/* ================================================= */}
              {/* SUMMARY */}
              {/* ================================================= */}

              {category &&
                gender &&
                ageGroup &&
                selectedWeight && (

                  <div className="mb-5 p-4 rounded-xl bg-neutral-900 border border-yellow-500/30">

                    <div className="text-yellow-400 text-[9px] uppercase font-black tracking-widest mb-3">
                      Registration Summary
                    </div>

                    <div className="grid grid-cols-3 gap-3 text-center">

                      {/* CATEGORY */}

                      <div>

                        <div className="text-[8px] text-neutral-600 uppercase">
                          Category
                        </div>

                        <div className="text-xs font-black uppercase mt-1">
                          {category ===
                          "altan_garaa"
                            ? "Алтан гараа"
                            : "Үндсэн"}
                        </div>

                      </div>

                      {/* GENDER */}

                      <div>

                        <div className="text-[8px] text-neutral-600 uppercase">
                          Gender
                        </div>

                        <div className="text-xs font-black uppercase mt-1">
                          {gender}
                        </div>

                      </div>

                      {/* WEIGHT */}

                      <div>

                        <div className="text-[8px] text-neutral-600 uppercase">
                          Weight
                        </div>

                        <div className="text-xs font-black mt-1">

                          {getWeightName(
                            currentWeights.find(
                              (w) =>
                                String(
                                  getWeightId(
                                    w
                                  )
                                ) ===
                                String(
                                  selectedWeight
                                )
                            )
                          )}

                        </div>

                      </div>

                    </div>

                    {/* SELECTED FIGHTER */}

                    {isClubCoach &&
                      selectedFighter && (

                        <div className="mt-4 pt-4 border-t border-neutral-800 text-center">

                          <div className="text-[8px] text-neutral-600 uppercase">
                            Fighter
                          </div>

                          <div className="text-sm font-black uppercase text-yellow-400 mt-1">
                            {
                              selectedFighter.name
                            }{" "}
                            {
                              selectedFighter.surname
                            }
                          </div>

                        </div>

                      )}

                  </div>

                )}

              {/* ================================================= */}
              {/* REGISTER BUTTON */}
              {/* ================================================= */}

              <button
                type="button"
                onClick={submit}
                disabled={
                  submitting ||
                  loadingWeights ||
                  loadingCoach ||
                  !category ||
                  !gender ||
                  !ageGroup ||
                  !selectedWeight ||
                  (isClubCoach &&
                    !selectedFighterId)
                }
                className="w-full bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black uppercase py-4 rounded-xl transition"
              >

                {submitting
  ? adminEditMode
    ? "Updating..."
    : isClubCoach
      ? "Register Fighter"
      : "Register for Tournament"
  : adminEditMode
    ? "Update Registration"
    : isClubCoach
      ? "Register Fighter"
      : "Register for Tournament"}

              </button>
</div>
            </>

          )}

        </div>
      </div>
    </div>
  );
}